import { forwardRef } from '@heroui/system';
import { MdOutlineCancel } from 'react-icons/md';
import { Chip, ChipProps, extendVariants } from '@heroui/react';

export const CustomChip = extendVariants(Chip, {
  variants: {
    variant: {
      dot: {
        base: 'border-0',
        dot: 'm-0',
      },
    },
    color: {
      default: {
        base: 'bg-base-60 text-base-10 hover:bg-base-40',
        closeButton: 'text-base-10',
      },
      primary: {
        base: 'bg-base-10 text-base-90 hover:bg-base-5',
        closeButton: 'text-base-90',
      },
      secondary: {
        base: 'bg-white hover:bg-white text-base-5',
      },
      tertiary: {
        base: 'bg-base-50 text-base-5 hover:bg-base-40',
      },
      info: {
        base: 'bg-info-70 text-info-10',
      },
      warning: {
        base: 'bg-warning-70 text-warning-10',
      },
      danger: {
        base: 'bg-danger-70 text-danger-10',
      },
      success: {
        base: 'bg-success-70 text-success-5',
      },
      relief: {
        base: 'bg-info-70 text-info-5',
      },
      proctor: {
        base: 'bg-base-40 text-base-5',
      },
      shadow: {
        base: 'bg-base-55 text-base-10',
      },
    },
    size: {
      xxs: {
        base: 'flex h-4.25 gap-1 items-center px-1',
        content: 'flex items-center text-s-r! text-black px-0 min-w-0',
      },
      xs: {
        base: 'flex h-5 gap-1 items-center [&>svg]:w-2 [&>svg]:h-2 px-2',
        content: 'flex items-center text-s-m! gap-1 px-0 min-w-0',
        closeButton: 'flex items-center opacity-100 [&>svg]:w-3 [&>svg]:h-3',
      },
      sm: {
        base: 'flex h-6 gap-1 items-center [&>svg]:w-4 [&>svg]:h-4 px-3',
        content: 'flex items-center text-m-m! gap-1 px-0 min-w-0',
        closeButton: 'flex items-center opacity-100 [&>svg]:w-4 [&>svg]:h-4',
      },
      md: {
        base: 'flex h-8 gap-2 items-center [&>svg]:w-5 [&>svg]:h-5 px-4',
        content: 'flex items-center text-l-m! gap-2 px-0 min-w-0',
        closeButton: 'flex items-center opacity-100 [&>svg]:w-5 [&>svg]:h-5',
      },
    },
  },
  defaultVariants: {
    color: 'default',
    size: 'md',
  },
});

export interface CustomChipProps extends Omit<ChipProps, 'color' | 'size' | 'ref'> {
  color?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'info'
    | 'warning'
    | 'danger'
    | 'success'
    | 'relief'
    | 'proctor'
    | 'shadow';
  size?: 'lg' | 'md' | 'sm' | 'xs' | 'xxs';
  count?: number;
  showCount?: boolean;
  dotStyles?: string;
}

const TmdxChip = forwardRef<'div', CustomChipProps>((props, ref) => {
  const { count, showCount, children, onClose, startContent, dotStyles, ...restProps } = props;
  const closeButton = onClose ? <MdOutlineCancel /> : null;

  return (
    <CustomChip
      ref={ref}
      classNames={{
        base: [
          'tmdx-chip',
          startContent && closeButton && 'px-1.5',
          startContent && !closeButton && 'pl-1.5 pr-2',
          !startContent && closeButton && 'pl-2 pr-1.5',
        ],
        dot: dotStyles,
      }}
      onClose={onClose}
      startContent={startContent}
      endContent={closeButton}
      aria-label="Chip"
      {...restProps}
    >
      {children} {showCount && count !== undefined && <span>({count})</span>}
    </CustomChip>
  );
});

TmdxChip.displayName = 'TmdxChip';

export default TmdxChip;
